import notificationWithIcon from '@/utils/notification';
import apiCall from '@/store/apiCall';
import isUrl from '@/utils/isUrl';
import URLS from '@/store/urls';

const uploadSingleFile = async (file, folder = 'page-content') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);
  const response = await apiCall({
    type: 'POST',
    headers: { 'content-type': 'multipart/form-data' },
    url: URLS.fileUpload,
    body: formData,
    isToken: true,
  });
  return response.data.data.url;
};

const actions = {
  async getPageContent({ commit }, page) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.pageContent}/${page}`,
        isToken: true,
      });
      commit('GET_PAGE_CONTENT_SUCCESS', data);
    } catch (err) {
      if (!err.message.includes('404')) {
        const { response: { data: { message } } = { data: { message: err.message } } } = err;
        notificationWithIcon('error', message);
      }
    }
  },
  async checkPageContent(_, page) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.pageContent}/${page}`,
        isToken: true,
      });
      return data;
    } catch {
      return [];
    }
  },
  async getGalleries({ commit }, page) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.pageContent}/${page}`,
        isToken: true,
      });
      const galleries = Array.isArray(data)
        ? data.filter((item) => item.name === 'folder')
        : [];
      commit('SET_GALLERIES', galleries);
    } catch (err) {
      if (!err.message.includes('404')) {
        const { response: { data: { message } } = { data: { message: err.message } } } = err;
        notificationWithIcon('error', message);
      }
    }
  },
  async getCollectionFolders({ commit }, page) {
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.pageContent}/${page}`,
        isToken: true,
      });
      commit('SET_COLLECTION_FOLDERS', Array.isArray(data) ? data : []);
    } catch (err) {
      if (!err.message.includes('404')) {
        const { response: { data: { message } } = { data: { message: err.message } } } = err;
        notificationWithIcon('error', message);
      }
    }
  },
  async createPageContent({ dispatch }, payload) {
    const { page, params, silent } = payload;
    try {
      await apiCall({
        type: 'POST',
        url: URLS.pageContent,
        body: params,
        isToken: true,
      });
      if (!silent) {
        notificationWithIcon('success', 'Content is saved');
      }
      dispatch('getPageContent', page);
      dispatch('getCollectionFolders', page);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async editPageContent({ dispatch }, payload) {
    try {
      const {
        id,
        page,
        params,
        silent,
      } = payload;
      await apiCall({
        type: 'PUT',
        url: `${URLS.pageContent}/${id}`,
        body: params,
        isToken: true,
      });
      if (!silent) {
        notificationWithIcon('success', 'Content is edited');
      }
      dispatch('getPageContent', page);
      dispatch('getCollectionFolders', page);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async createOrEditPageContent({ dispatch }, payload) {
    try {
      const {
        id,
        page,
        params,
      } = payload;
      const {
        imageUrl,
        imageUrlMobile,
      } = params;
      const images = [imageUrl[0], imageUrlMobile[0]];
      const [desktopImage, mobileImage] = await Promise.all(images.map((image) => {
        if (isUrl(image)) {
          return image;
        }
        if (typeof image !== 'string' && !(image && Object.keys(image).length)) {
          return '';
        }
        if (image && image.file) {
          const { file } = image;
          const formData = new FormData();
          formData.append('file', file);
          formData.append('folder', 'default');
          return apiCall({
            type: 'POST',
            headers: { 'content-type': 'multipart/form-data' },
            url: URLS.fileUpload,
            body: formData,
            isToken: true,
          });
        }
        return '';
      }));
      const urlDesktopImage = typeof desktopImage === 'string'
        ? desktopImage
        : desktopImage.data.data.url;
      const urlMobileImage = typeof mobileImage === 'string'
        ? mobileImage
        : mobileImage.data.data.url;
      const newParams = {
        params: {
          ...params,
          imageUrl: urlDesktopImage,
          imageUrlMobile: urlMobileImage,
        },
        id,
        page,
      };
      if (id) {
        dispatch('editPageContent', newParams);
      } else {
        delete newParams.id;
        dispatch('createPageContent', newParams);
      }
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async deletePageContent({ dispatch }, payload) {
    try {
      const { id, page } = payload;
      await apiCall({
        type: 'DELETE',
        url: `${URLS.pageContent}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Block is empty');
      dispatch('getPageContent', page);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async deleteSlide(_, id) {
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.pageContentBlock}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Slide is deleted');
    } catch (err) {
      if (!err.message.includes('404')) {
        const { response: { data: { message } } = { data: { message: err.message } } } = err;
        notificationWithIcon('error', message);
      }
    }
  },
  async createSlide({ dispatch }, payload) {
    try {
      const {
        page,
        ...rest
      } = payload;
      await apiCall({
        type: 'POST',
        url: URLS.pageContentBlock,
        body: rest,
        isToken: true,
      });
      notificationWithIcon('success', 'Slide is saved');
      dispatch('getPageContent', page);
      dispatch('getCollectionFolders', page);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async editSlide({ dispatch }, payload) {
    try {
      const {
        id,
        page,
        notifyText,
        ...rest
      } = payload;
      await apiCall({
        type: 'PUT',
        url: `${URLS.pageContentBlock}/${id}`,
        body: rest,
        isToken: true,
      });
      notificationWithIcon('success', notifyText || 'Slide is edited');
      dispatch('getPageContent', page);
      dispatch('getCollectionFolders', page);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async createOrEditSlide({ dispatch }, payload) {
    try {
      const {
        imageUrl,
        imageUrlMobile,
      } = payload;
      const images = [imageUrl[0], imageUrlMobile[0]];
      const [desktopImage, mobileImage] = await Promise.all(images.map((image) => {
        if (isUrl(image)) {
          return image;
        }
        if (typeof image !== 'string' && !(image && Object.keys(image).length)) {
          return '';
        }
        if (image && image.file) {
          const { file } = image;
          const formData = new FormData();
          formData.append('file', file);
          formData.append('folder', 'default');
          return apiCall({
            type: 'POST',
            headers: { 'content-type': 'multipart/form-data' },
            url: URLS.fileUpload,
            body: formData,
            isToken: true,
          });
        }
        return '';
      }));
      const urlDesktopImage = typeof desktopImage === 'string'
        ? desktopImage
        : desktopImage.data.data.url;
      const urlMobileImage = typeof mobileImage === 'string'
        ? mobileImage
        : mobileImage.data.data.url;
      const newParams = {
        ...payload,
        imageUrl: urlDesktopImage,
        imageUrlMobile: urlMobileImage,
      };
      if (payload.id) {
        delete newParams.contentId;
        dispatch('editSlide', newParams);
      } else {
        delete newParams.id;
        dispatch('createSlide', newParams);
      }
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async uploadPageContentFolderImages({ dispatch }, payload) {
    try {
      const {
        newImages, page, contentId, order,
      } = payload;
      // Filter only images with valid file (newly uploaded, not existing)
      const imagesToUpload = newImages.filter((image) => image.file);
      if (imagesToUpload.length === 0) {
        return;
      }
      const images = await Promise.all(imagesToUpload.map((image) => {
        const { file } = image;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'default');
        return apiCall({
          type: 'POST',
          headers: { 'content-type': 'multipart/form-data' },
          url: URLS.fileUpload,
          body: formData,
          isToken: true,
        });
      }));
      await Promise.all(images.map((image, index) => apiCall({
        type: 'POST',
        url: URLS.pageContentBlock,
        body: {
          imageUrl: image.data.data.url,
          imageUrlMobile: image.data.data.url,
          order: order + index,
          contentId,
        },
        isToken: true,
      })));
      dispatch('getCollectionFolders', page);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async uploadModuleImages(_, payload) {
    try {
      const {
        desktopFile,
        mobileFile,
        currentImage,
        currentImageMobile,
      } = payload;
      let desktopUrl = currentImage || '';
      let mobileUrl = currentImageMobile || '';

      if (desktopFile?.file) {
        desktopUrl = await uploadSingleFile(desktopFile.file);
      } else if (desktopFile?.url && !desktopFile.url.startsWith('data:')) {
        desktopUrl = desktopFile.url;
      }

      if (mobileFile?.file) {
        mobileUrl = await uploadSingleFile(mobileFile.file);
      } else if (mobileFile?.url && !mobileFile.url.startsWith('data:')) {
        mobileUrl = mobileFile.url;
      }

      return { image: desktopUrl, imageMobile: mobileUrl };
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message || 'Failed to upload image');
      throw err;
    }
  },
  async deleteFolder({ dispatch }, payload) {
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.pageContent}/${payload.id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Folder deleted');
      dispatch('getCollectionFolders', payload.page);
    } catch (err) {
      if (!err.message.includes('404')) {
        const { response: { data: { message } } = { data: { message: err.message } } } = err;
        notificationWithIcon('error', message);
      }
    }
  },
  async deleteFolderImage({ dispatch }, payload) {
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.pageContentBlock}/${payload.id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Image deleted');
      dispatch('getCollectionFolders', payload.page);
    } catch (err) {
      if (!err.message.includes('404')) {
        const { response: { data: { message } } = { data: { message: err.message } } } = err;
        notificationWithIcon('error', message);
      }
    }
  },
  async editPageContentMultiFolders({ dispatch }, payload) {
    try {
      const {
        contents,
        page,
      } = payload;
      await Promise.all((contents).map(({ id, ...rest }) => (
        apiCall({
          type: 'PUT',
          url: `${URLS.pageContent}/${id}`,
          body: rest,
          isToken: true,
        })
      )));
      notificationWithIcon('success', 'Content is edited');
      dispatch('getCollectionFolders', page);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
};

export default actions;
